import { useCarousel } from "@aleksundshantu/aunds-utils-react/primitives/carousel";
import { EmblaCarouselType, EmblaEventType } from "embla-carousel";
import {
  useContext,
  ReactNode,
  useState,
  useEffect,
  createContext,
  useCallback,
  useRef,
} from "react";

// Context for sharing carousel state
interface CarouselExtraContextType {
  activeIndex: number;
  emblaApi: EmblaCarouselType | undefined;
  scrollSnaps: number[];
}

const CarouselExtraContext = createContext<CarouselExtraContextType | null>(
  null,
);

// Hook to use the context
export const useCarouselExtraContext = () => {
  const context = useContext(CarouselExtraContext);
  if (!context) {
    throw new Error(
      "useCarouselExtraContext must be used within CarouselExtraProvider",
    );
  }
  return context;
};

interface CarouselExtraProviderProps {
  children: ReactNode;
  enableTween?: boolean;
  tweenSelector?: string;
}

// const TWEEN_FACTOR_BASE = 0.375;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

export const CarouselExtraProvider = ({
  children,
  enableTween = false,
  tweenSelector = "", //class name of the element to be tweened
}: CarouselExtraProviderProps) => {
  const { emblaApi } = useCarousel();
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLElement[]>([]);

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const setTweenNodes = useCallback(
    (emblaApi: EmblaCarouselType): void => {
      tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
        return slideNode.querySelector(tweenSelector) as HTMLElement;
      });
    },
    [tweenSelector],
  );

  const setTweenFactor = useCallback(() => {
    // tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
    tweenFactor.current = 0.375;
  }, []);

  const tweenScale = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === "scroll";
      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
          const scale = numberWithinRange(tweenValue, 0, 1).toString();
          const tweenNode = tweenNodes.current[slideIndex];
          if (!tweenNode) return;
          tweenNode.style.transform = `scale(${scale})`;
        });
      });
    },
    [],
  );

  // const tweenScale = useCallback((emblaApi: EmblaCarouselType) => {
  //   const engine = emblaApi.internalEngine();
  //   const scrollLocation = engine.location.get();
  //   const slides = emblaApi.slideNodes();

  //   slides.forEach((_, slideIndex) => {
  //     let diffToTarget = engine.scrollSnaps[slideIndex] - scrollLocation;

  //     if (engine.options.loop) {
  //       engine.slideLooper.loopPoints.forEach((loopItem) => {
  //         const target = loopItem.target();
  //         if (slideIndex === loopItem.index && target !== 0) {
  //           const sign = Math.sign(target);
  //           if (sign === -1)
  //             diffToTarget =
  //               engine.scrollSnaps[slideIndex] - (1 + scrollLocation);
  //           if (sign === 1)
  //             diffToTarget =
  //               engine.scrollSnaps[slideIndex] + (1 - scrollLocation);
  //         }
  //       });
  //     }

  //     // Math for 40% width:
  //     // Distance between slides is 0.4.
  //     // We want a 15% scale reduction (1.0 -> 0.85).
  //     // 0.15 / 0.4 = 0.375.
  //     const tweenValue = 1 - Math.abs(diffToTarget * 0.375);
  //     const scale = numberWithinRange(tweenValue, 0.85, 1).toString();
  //     console.log("scale", scale, "diffToTarget", diffToTarget);
  //     const tweenNode = tweenNodes.current[slideIndex];
  //     if (tweenNode) {
  //       tweenNode.style.transform = `scale(${scale})`;
  //     }
  //   });
  // }, []);
  const tweenTransformOrigin = (emblaApi: EmblaCarouselType) => {
    const slides = emblaApi.slideNodes();
    const slideCount = slides.length;
    if (!slideCount) return;

    const currentIndex = numberWithinRange(activeIndex, 0, slideCount - 1);

    const leftIndex = currentIndex === 0 ? slideCount - 1 : currentIndex - 1;
    const rightIndex = currentIndex === slideCount - 1 ? 0 : currentIndex + 1;

    const leftSlideNode = slides[leftIndex]?.querySelector(
      tweenSelector,
    ) as HTMLElement | null;

    const rightSlideNode = slides[rightIndex]?.querySelector(
      tweenSelector,
    ) as HTMLElement | null;

    if (leftSlideNode) {
      leftSlideNode.style.transformOrigin = "left top";
    }

    if (rightSlideNode) {
      rightSlideNode.style.transformOrigin = "right top";
    }

    slides.forEach((slide, index) => {
      const node = slide.querySelector(tweenSelector) as HTMLElement | null;
      if (!node) return;
      node.dataset.active = index === currentIndex ? "true" : "false";
    });
  };

  useEffect(() => {
    if (!emblaApi) return;

    const update = () => {
      setActiveIndex(emblaApi.selectedScrollSnap());
    };

    update();
    onInit(emblaApi);
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
    emblaApi.on("reInit", onInit);

    if (enableTween) {
      setTweenNodes(emblaApi);
      setTweenFactor();
      tweenScale(emblaApi);

      emblaApi
        .on("reInit", setTweenNodes)
        .on("reInit", setTweenFactor)
        .on("reInit", tweenScale)
        .on("scroll", tweenScale)
        .on("slideFocus", tweenScale);
    }

    return () => {
      emblaApi.off("select", update);
      emblaApi.off("reInit", update);
      emblaApi.off("reInit", onInit);
      if (enableTween) {
        emblaApi.off("reInit", setTweenNodes);
        emblaApi.off("reInit", setTweenFactor);
        emblaApi.off("reInit", tweenScale);
        emblaApi.off("scroll", tweenScale);
        emblaApi.off("slideFocus", tweenScale);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi]);

  useEffect(() => {
    if (enableTween && emblaApi) {
      tweenTransformOrigin(emblaApi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, emblaApi]);

  return (
    <CarouselExtraContext.Provider
      value={{ activeIndex, emblaApi, scrollSnaps }}
    >
      {children}
    </CarouselExtraContext.Provider>
  );
};
